import React, { useState, useEffect } from 'react';
import { Clock, DollarSign, ShoppingBag, TrendingUp, PlayCircle, StopCircle } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { translations } from '../utils/translations';
import { shiftApi } from '../api';
import ShiftStartDialog from '../components/shift/ShiftStartDialog';
import ShiftEndDialog from '../components/shift/ShiftEndDialog';
import ShiftHistoryTable from '../components/shift/ShiftHistoryTable';

const ShiftPage = () => {
  const { language, currentUser } = useApp();
  const t = translations[language];

  const [currentShift, setCurrentShift] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDialogOpen, setStartDialogOpen] = useState(false);
  const [endDialogOpen, setEndDialogOpen] = useState(false);

  useEffect(() => {
    loadCurrentShift();
    loadHistory();
  }, []);

  const loadCurrentShift = async () => {
    try {
      const res = await shiftApi.getCurrent();
      if (res.success && res.data) setCurrentShift(res.data);
    } catch (err) { console.error('获取当前班次失败:', err); }
  };

  const loadHistory = async () => {
    setLoading(true);
    try {
      const res = await shiftApi.getHistory({ limit: 10 });
      if (res.success) setHistory(res.data || []);
    } catch (err) { console.error('获取班次历史失败:', err); }
    finally { setLoading(false); }
  };

  const handleStartShift = async (data) => {
    try {
      const res = await shiftApi.start(data);
      if (res.success) {
        alert(language === 'en' ? 'Shift started successfully' : language === 'km' ? 'ចាប់ផ្តើមវេនបានជោគជ័យ' : '班次开始成功');
        setStartDialogOpen(false);
        loadCurrentShift();
      }
    } catch (err) {
      alert(language === 'en' ? 'Failed to start shift' : language === 'km' ? 'បរាជ័យក្នុងការចាប់ផ្តើមវេន' : '开始班次失败');
    }
  };

  const handleEndShift = async (data) => {
    try {
      const res = await shiftApi.end(data);
      if (res.success) {
        alert(language === 'en' ? 'Shift ended successfully' : language === 'km' ? 'បញ្ចប់វេនបានជោគជ័យ' : '班次结束成功');
        setEndDialogOpen(false);
        setCurrentShift(null);
        loadHistory();
      }
    } catch (err) {
      alert(language === 'en' ? 'Failed to end shift' : language === 'km' ? 'បរាជ័យក្នុងការបញ្ចប់វេន' : '结束班次失败');
    }
  };

  const formatDuration = (startTime) => {
    const diff = new Date() - new Date(startTime);
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}${language === 'en' ? 'h' : language === 'km' ? 'ម៉ោង' : '小时'} ${minutes}${language === 'en' ? 'm' : language === 'km' ? 'នាទី' : '分钟'}`;
  };

  return (
    <div className="h-full flex flex-col gap-4">

      {/* 头部：标题 + 结束/开始班次按钮 */}
      <div className="flex-shrink-0 flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold">
            {t.shift || (language === 'en' ? 'Shift Management' : language === 'km' ? 'គ្រប់គ្រងវេន' : '班次管理')}
          </h2>
          <p className="text-xs text-gray-600 mt-1">
            {language === 'en' ? 'Manage your work shifts and cash register' :
             language === 'km' ? 'គ្រប់គ្រងវេនការងារនិងទឹកប្រាក់របស់អ្នក' :
             '管理您的工作班次和收银台'}
          </p>
        </div>
        {currentShift ? (
          <button onClick={() => setEndDialogOpen(true)}
            className="inline-flex items-center space-x-1 bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-600 transition-colors text-sm font-medium">
            <StopCircle className="w-4 h-4" />
            <span>{language === 'en' ? 'End Shift' : language === 'km' ? 'បញ្ចប់វេន' : '结束班次'}</span>
          </button>
        ) : (
          <button onClick={() => setStartDialogOpen(true)}
            className="inline-flex items-center space-x-1 bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium">
            <PlayCircle className="w-4 h-4" />
            <span>{language === 'en' ? 'Start Shift' : language === 'km' ? 'ចាប់ផ្តើមវេន' : '开始班次'}</span>
          </button>
        )}
      </div>

      {/* 当前班次 */}
      <div className="flex-shrink-0">
        {currentShift ? (
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-5 text-white">
            {/* 班次信息 + 统计卡片在同一行 */}
            <div className="flex items-center gap-4">
              {/* 左侧：班次信息 */}
              <div className="flex-shrink-0 w-48">
                <div className="flex items-center space-x-2 mb-1">
                  <Clock className="w-4 h-4" />
                  <h3 className="text-sm font-bold">
                    {language === 'en' ? 'Current Shift' : language === 'km' ? 'វេនបច្ចុប្បន្ន' : '当前班次'}
                  </h3>
                </div>
                <p className="text-blue-100 text-xs">
                  {language === 'en' ? 'Cashier' : '收银员'}: {currentShift.cashier_name}
                </p>
                <p className="text-blue-100 text-xs">
                  {language === 'en' ? 'Start' : '开始'}: {new Date(currentShift.start_time).toLocaleString()}
                </p>
                <p className="text-blue-100 text-xs">
                  {language === 'en' ? 'Duration' : '时长'}: {formatDuration(currentShift.start_time)}
                </p>
              </div>

              {/* 右侧：统计卡片 */}
              <div className="flex-1 grid grid-cols-3 gap-5">
                {[
                  { label: language === 'en' ? 'Opening Cash' : language === 'km' ? 'ទឹកប្រាក់បើក' : '期初金额', value: `$${parseFloat(currentShift.opening_cash || 0).toFixed(2)}`, icon: DollarSign },
                  { label: language === 'en' ? 'Total Sales' : language === 'km' ? 'ការលក់សរុប' : '销售总额', value: `$${parseFloat(currentShift.total_sales || 0).toFixed(2)}`, icon: TrendingUp },
                  { label: language === 'en' ? 'Transactions' : language === 'km' ? 'ប្រតិបត្តិការ' : '交易数量', value: currentShift.transaction_count || 0, icon: ShoppingBag },
                ].map(({ label, value, icon: Icon }) => (
                  <div key={label} className="bg-white/10 rounded-lg p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-blue-100 text-xs">{label}</p>
                        <p className="text-lg font-bold mt-0.5">{value}</p>
                      </div>
                      <Icon className="w-5 h-5 text-blue-200" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl p-6 text-center">
            <Clock className="w-10 h-10 mx-auto text-gray-400 mb-2" />
            <h3 className="text-sm font-semibold text-gray-700 mb-1">
              {language === 'en' ? 'No Active Shift' : language === 'km' ? 'គ្មានវេនសកម្ម' : '没有活动班次'}
            </h3>
            <p className="text-xs text-gray-500">
              {language === 'en' ? 'Click top right to start a shift' : language === 'km' ? 'ចុចខាងស្តាំខាងលើដើម្បីចាប់ផ្តើម' : '请点击右上角开始班次'}
            </p>
          </div>
        )}
      </div>

      {/* 班次历史 */}
      <div className="flex-1 min-h-0 bg-white rounded-xl shadow flex flex-col">
        <div className="flex-shrink-0 px-6 py-4 border-b">
          <h3 className="text-base font-bold">
            {language === 'en' ? 'Shift History' : language === 'km' ? 'ប្រវត្តិវេន' : '班次历史'}
          </h3>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <ShiftHistoryTable shifts={history} />
          )}
        </div>
      </div>

      {startDialogOpen && (
        <ShiftStartDialog onClose={() => setStartDialogOpen(false)} onSubmit={handleStartShift} />
      )}
      {endDialogOpen && currentShift && (
        <ShiftEndDialog shift={currentShift} onClose={() => setEndDialogOpen(false)} onSubmit={handleEndShift} />
      )}
    </div>
  );
};

export default ShiftPage;